import { useState } from "react";

import { http, qs } from "../api";
import { Badge, Banner, Confirm, Empty, Field, useAsync, useToast } from "../ui";
import { BarChartCard, DonutCard, type BarDatum } from "../charts";
import {
  Card,
  ErrorBanner,
  Loadable,
  MIN_CELL_N,
  Meter,
  MetricCard,
  Tagish,
  axisLabel,
  dash,
  fmtInt,
  fmtMs,
  fmtNum3,
  fmtPct,
  num,
  rateColor,
  wasteColor,
} from "./format";
import type { AlignmentSummary, EscalationsResponse, StrataResponse, SuiteDetail } from "./types";

// ============================================================ analysis tab

/**
 * What the headline number is hiding, in the order the answers change a
 * decision: is the GRADER trustworthy, WHICH axis carries the failures, WHERE
 * the LLM budget goes, WHAT broke, and is the corpus even shaped like traffic.
 */
export function AnalysisTab({
  detail,
  runId,
  canWrite,
}: {
  detail: SuiteDetail | null;
  runId: string;
  canWrite: boolean;
}) {
  const toast = useToast();
  const judge = detail?.judge ?? {};
  const necessity = detail?.necessity ?? {};
  const breakdown = detail?.axis_breakdown ?? {};
  const breakdownAxes = Object.keys(breakdown).filter((a) => (breakdown[a] ?? []).length > 1);
  const operations = necessity.operations ?? [];

  const alignment = useAsync<AlignmentSummary>(
    () =>
      runId
        ? http.get<AlignmentSummary>(`/chatbot/eval-ops/alignment${qs({ run_id: runId })}`)
        : Promise.resolve({ dimensions: [] }),
    [runId]
  );
  const strata = useAsync<StrataResponse>(
    () =>
      runId
        ? http.get<StrataResponse>(`/chatbot/eval-ops/strata${qs({ run_id: runId })}`)
        : Promise.resolve({ run_id: "", strata: [] }),
    [runId]
  );
  const escalations = useAsync<EscalationsResponse>(
    () => http.get<EscalationsResponse>("/chatbot/eval-ops/escalations"),
    []
  );

  // Escalating a stratum changes how the router treats live traffic, so it goes
  // through Confirm even though the store is append-only.
  const [pending, setPending] = useState<{ key: string; escalate: boolean; rate: number; n: number } | null>(null);
  const [reason, setReason] = useState("");

  const applyEscalation = async () => {
    if (!pending) return;
    await http.post("/chatbot/eval-ops/escalations", {
      strata_key: pending.key,
      escalated: pending.escalate,
      error_rate: pending.rate,
      n: pending.n,
      reason: reason.trim(),
    });
    toast.push(`${pending.escalate ? "Escalated" : "Cleared"} ${pending.key}`, "good");
    setReason("");
    escalations.reload();
  };

  const escalatedKeys = new Map((escalations.data?.escalations ?? []).map((e) => [e.strata_key, e]));

  const RECALL_GATE = 0.98;

  const failureBars: BarDatum[] = (detail?.failure_modes ?? [])
    .slice(0, 12)
    .map((m) => ({ label: m.failure_mode, value: num(m.n) }));

  const verdictSlices: BarDatum[] = (necessity.by_verdict ?? []).map((v) => ({
    label: v.verdict,
    value: num(v.turns),
  }));

  return (
    <div className="stack">
      <Card
        title="Is the grader trustworthy?"
        note="Both graders ran on every turn. The deterministic scorer gates; the LLM judge advises. Where they disagree is where one of them is wrong — and which one is the question worth a human."
      >
        {!num(judge.judged_n) ? (
          <Empty
            title="No judged turns in this run"
            body={`${fmtInt(judge.unjudged)} unjudged, ${fmtInt(
              judge.skipped
            )} skipped. Every number on this screen is the deterministic scorer alone.`}
          />
        ) : (
          <>
            <div className="grid c4">
              <MetricCard
                label="Agreement"
                value={fmtPct(judge.agreement)}
                sub={`${fmtInt(judge.agree)} / ${fmtInt(judge.judged_n)} judged`}
              />
              <MetricCard
                label="Hidden breakage"
                value={fmtInt(judge.false_pass)}
                sub="passed here, judge says wrong"
                tone={num(judge.false_pass) > 0 ? "bad" : "default"}
                hint="Gate passed the turn, judge says the reply was wrong. The suite is certifying this green."
              />
              <MetricCard
                label="Over-strict checks"
                value={fmtInt(judge.false_fail)}
                sub="failed here, judge says fine"
                tone={num(judge.false_fail) > 0 ? "warn" : "default"}
                hint="Gate failed the turn, judge says the reply was fine. Usually an over-strict pattern."
              />
              <MetricCard
                label="Skipped · unjudged"
                value={`${fmtInt(judge.skipped)} · ${fmtInt(judge.unjudged)}`}
                sub="excluded from agreement"
              />
            </div>
            <div className="row" style={{ flexWrap: "wrap", gap: "var(--sp-4)", marginTop: "var(--sp-3)", fontSize: 12 }}>
              <span className="muted">
                macro-F1 <span className="strong num">{fmtNum3(judge.macro_f1)}</span> over{" "}
                {fmtInt(judge.n_segments)} row segments
              </span>
              <span className="muted">
                weighted <span className="num">{fmtNum3(judge.weighted_f1)}</span>
              </span>
              <span className="muted">
                micro <span className="num">{fmtNum3(judge.micro_f1)}</span>
              </span>
              <span className="muted">
                precision <span className="num">{fmtNum3(judge.precision)}</span>
              </span>
              <span className="muted">
                recall <span className="num">{fmtNum3(judge.recall)}</span>
              </span>
            </div>
            {judge.definition && (
              <p className="muted" style={{ fontSize: 11, marginTop: "var(--sp-2)" }}>
                {judge.definition}
              </p>
            )}
          </>
        )}
      </Card>

      <Card
        title="Which axis carries the failures"
        note="Every axis this run actually recorded, each value with its own n. Axes the harness left empty are omitted — a single '(blank)' row is a missing field, not a segment."
      >
        {!breakdownAxes.length ? (
          <Empty title="Nothing to segment" body="This run recorded no axis with more than one distinct value." />
        ) : (
          <div className="grid c2">
            {breakdownAxes.map((axis) => (
              <div key={axis}>
                <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  {axisLabel(axis)}
                </h4>
                <div className="table-wrap">
                  <table className="data">
                    <tbody>
                      {(breakdown[axis] ?? []).map((row) => (
                        <tr key={`${axis}-${row.value}`}>
                          <td className="truncate" style={{ maxWidth: 160 }} title={row.value || "(blank)"}>
                            {row.value || "(blank)"}
                          </td>
                          <td style={{ width: 120 }}>
                            <Meter
                              value={num(row.pass_rate)}
                              color={num(row.n) < MIN_CELL_N ? "var(--text-3)" : rateColor(row.pass_rate, num(row.n))}
                            />
                          </td>
                          <td className="num" style={{ color: rateColor(row.pass_rate, num(row.n)) }}>
                            {fmtPct(row.pass_rate, 0)}
                          </td>
                          <td className="num muted">n={fmtInt(row.n)}</td>
                          <td className="num muted">{fmtMs(row.p95_ms)} p95</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card
        title="Avoidable LLM calls — where the budget goes"
        note={
          <>
            Every LLM call this run recorded, judged deterministically: routing, translation, summarisation and
            cache-answered calls are avoidable by construction; a genuinely novel answer is not. Ranked by tokens
            wasted, because that is the fix order.
            {necessity.instrumented ? (
              <>
                {" "}
                <span className="strong">
                  {fmtInt(necessity.judged_turns)} turns judged · {fmtInt(operations.length)} operation/verdict pairs
                </span>
              </>
            ) : null}
          </>
        }
      >
        {!necessity.instrumented ? (
          <Banner tone="warning">
            Not instrumented for this run — {necessity.note ?? "no turn carries a necessity verdict"}. This is not a
            claim that nothing was avoidable.
          </Banner>
        ) : (
          <>
            <div className="grid c4">
              <MetricCard
                label="Avoidable share"
                value={fmtPct(necessity.avoidable_share, 1)}
                sub={`${fmtInt(necessity.avoidable_tokens)} of ${fmtInt(necessity.judged_tokens)} judged tokens`}
                tone={
                  necessity.avoidable_share === null || necessity.avoidable_share === undefined
                    ? "default"
                    : Number(necessity.avoidable_share) >= 0.25
                      ? "bad"
                      : Number(necessity.avoidable_share) >= 0.1
                        ? "warn"
                        : "good"
                }
              />
              <MetricCard
                label="Avoidable calls"
                value={fmtInt(necessity.avoidable_calls)}
                sub={
                  num(necessity.unattributed_calls)
                    ? `${fmtInt(necessity.unattributed_calls)} calls have no per-call record`
                    : "every counted call was attributed"
                }
              />
              <MetricCard
                label="% zero-LLM"
                value={fmtPct(necessity.pct_zero_llm, 0)}
                sub={`${fmtInt(necessity.zero_llm_turns)} of ${fmtInt(
                  necessity.zero_llm_denominator
                )} metered turns`}
              />
              <div className="stat">
                <div className="label">Verdict mix</div>
                <div className="chips" style={{ marginTop: 6 }}>
                  {(necessity.by_verdict ?? []).map((v) => (
                    <Tagish key={v.verdict}>
                      {v.verdict} {fmtInt(v.turns)}
                    </Tagish>
                  ))}
                  {!(necessity.by_verdict ?? []).length && <span className="muted">—</span>}
                </div>
              </div>
            </div>

            {necessity.note && (
              <p className="muted" style={{ fontSize: 11, marginTop: "var(--sp-2)" }}>
                {necessity.note}
              </p>
            )}
            {/* A bare unjudged count invites the reader to assume those turns look
                like the judged ones; the reason says which input was missing. */}
            {(necessity.unjudged_reasons ?? []).map((r) => (
              <div key={r.reason} className="muted" style={{ fontSize: 11 }}>
                <span className="num strong">{fmtInt(r.turns)} turns unjudged</span> — {r.reason}
              </div>
            ))}

            {verdictSlices.length > 1 && (
              <div style={{ marginTop: "var(--sp-4)" }}>
                <DonutCard
                  title="Judged turns by necessity verdict"
                  data={verdictSlices}
                  centerCaption="judged turns"
                  note="Turns, not tokens — the spend ranking is the table below."
                />
              </div>
            )}

            {!operations.length ? (
              <Empty title="No per-call records" body="The judged turns carry no per-call detail." />
            ) : (
              <div className="table-wrap" style={{ marginTop: "var(--sp-3)" }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Operation</th>
                      <th>Verdict</th>
                      <th className="num">Calls</th>
                      <th className="num">Tokens</th>
                      <th>Share of judged spend</th>
                      <th>Example cases</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.map((op) => (
                      <tr key={`${op.operation}|${op.verdict}`}>
                        <td className="truncate" style={{ maxWidth: 200 }} title={op.reason ?? ""}>
                          {op.operation}
                        </td>
                        <td>
                          <Badge tone={op.avoidable ? "critical" : op.verdict === "unknown" ? "neutral" : "good"}>
                            {op.verdict}
                          </Badge>
                        </td>
                        <td className="num">{fmtInt(op.calls)}</td>
                        <td className="num" style={{ color: op.avoidable ? "var(--critical)" : undefined }}>
                          {fmtInt(op.tokens)}
                        </td>
                        <td>
                          <div className="row">
                            <div style={{ flex: 1, minWidth: 80 }}>
                              <Meter
                                value={num(op.token_share)}
                                color={op.avoidable ? "var(--critical)" : "var(--success)"}
                              />
                            </div>
                            <span className="num muted">{fmtPct(op.token_share, 1)}</span>
                          </div>
                        </td>
                        <td className="truncate muted" style={{ maxWidth: 220 }} title={(op.cases ?? []).join(", ")}>
                          {(op.cases ?? []).join(", ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Which SEGMENT wastes most: the same marginals, ranked by avoidable
                tokens. An axis nobody judged is left out rather than drawn as zeros. */}
            <div className="grid c2" style={{ marginTop: "var(--sp-4)" }}>
              {breakdownAxes
                .filter((axis) => (breakdown[axis] ?? []).some((r) => num(r.necessity_n) > 0))
                .map((axis) => (
                  <div key={`waste-${axis}`}>
                    <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                      Avoidable spend by {axisLabel(axis)}
                    </h4>
                    <div className="table-wrap">
                      <table className="data">
                        <tbody>
                          {[...(breakdown[axis] ?? [])]
                            .filter((r) => num(r.necessity_n) > 0)
                            .sort((a, b) => num(b.avoidable_tokens) - num(a.avoidable_tokens))
                            .map((row) => (
                              <tr key={`${axis}-${row.value}`}>
                                <td className="truncate" style={{ maxWidth: 160 }}>
                                  {row.value || "(blank)"}
                                </td>
                                <td style={{ width: 110 }}>
                                  <Meter value={num(row.avoidable_share)} color="var(--critical)" />
                                </td>
                                <td className="num" style={{ color: wasteColor(row.avoidable_share) }}>
                                  {row.avoidable_share === null || row.avoidable_share === undefined
                                    ? "unjudged"
                                    : fmtPct(row.avoidable_share, 0)}
                                </td>
                                <td className="num muted">
                                  {row.avoidable_tokens === null || row.avoidable_tokens === undefined
                                    ? "—"
                                    : `${fmtInt(row.avoidable_tokens)} tok`}
                                </td>
                                <td className="num muted">n={fmtInt(row.necessity_n)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </Card>

      <Card
        title="What broke — failure modes, ranked"
        note="Counted over every turn in the run, not the browsable sample. Soft findings are listed separately because they do not gate: a soft check reading 100% is not a P0 passing."
      >
        <div className="grid c2">
          <div>
            {!failureBars.length ? (
              <Banner tone="good">No failing check fired in this run.</Banner>
            ) : (
              <BarChartCard
                title="Hard failures (these gate)"
                data={failureBars}
                horizontal
                note="Top 12 by count."
              />
            )}
            {(detail?.failure_modes ?? []).slice(0, 12).map((mode) => (
              <div key={mode.failure_mode} className="muted" style={{ fontSize: 10, marginTop: 2 }}>
                <span className="strong">{mode.failure_mode}</span>{" "}
                {[...new Set(mode.cases ?? [])].join(", ") || "no example cases recorded"}
              </div>
            ))}
          </div>
          <div>
            <h4 className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
              Soft findings (do not gate)
            </h4>
            {!(detail?.soft_modes ?? []).length ? (
              <Empty title="None recorded" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <tbody>
                    {(detail?.soft_modes ?? []).map((mode) => (
                      <tr key={mode.soft_mode}>
                        <td style={{ color: "var(--warning)" }}>{mode.soft_mode}</td>
                        <td className="num">{fmtInt(mode.n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card
        title="Alignment with real traffic"
        note="The same statistic over this run's turns and over production turns, each carrying its own n. If the corpus is not shaped like real usage, every number above measures a population that does not exist."
      >
        <Loadable loading={alignment.loading} error={alignment.error} what="Failed to load alignment" cols={6}>
          {!(alignment.data?.dimensions ?? []).length ? (
            <Empty
              title="Not measured for this run"
              body="Alignment is computed service-side (only it can see the traces table); it appears here once the harness has posted one."
            />
          ) : (
            <>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Dimension</th>
                      <th>Eval</th>
                      <th>Real</th>
                      <th>Divergence</th>
                      <th className="num">n eval</th>
                      <th className="num">n real</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(alignment.data?.dimensions ?? []).map((row) => (
                      <tr key={row.dimension}>
                        <td title={row.detail?.metric ?? undefined}>{row.dimension}</td>
                        <td className="num">{dash(row.eval_value)}</td>
                        <td className="num">{dash(row.real_value)}</td>
                        <td>
                          <div className="row">
                            <div style={{ width: 80 }}>
                              <Meter
                                value={Math.min(1, num(row.divergence))}
                                color={
                                  !row.sufficient_sample
                                    ? "var(--text-3)"
                                    : num(row.divergence) <= 0.1
                                      ? "var(--success)"
                                      : num(row.divergence) <= 0.25
                                        ? "var(--warning)"
                                        : "var(--critical)"
                                }
                              />
                            </div>
                            <span className="num muted">{num(row.divergence).toFixed(3)}</span>
                          </div>
                        </td>
                        <td className="num">{fmtInt(row.n_eval)}</td>
                        <td className="num" style={{ color: row.sufficient_sample ? undefined : "var(--warning)" }}>
                          {fmtInt(row.n_real)}
                          {row.sufficient_sample ? "" : " ⚠"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted" style={{ fontSize: 11, marginTop: "var(--sp-2)" }}>
                Rows marked ⚠ have fewer than {fmtInt(alignment.data?.min_real_n)} real turns behind them: the number
                is shown, the verdict is withheld.
              </p>
            </>
          )}
        </Loadable>
      </Card>

      <Card
        title="Per-strata recall — detection cases"
        note={`From eval_results (single-message detection cases), not the turns above. Recall below the pinned ${fmtPct(
          RECALL_GATE,
          0
        )} gate is red; a stratum with no scam cases has no recall and shows a dash rather than a zero. Escalation state is the router's, and it is append-only.`}
      >
        <Loadable loading={strata.loading || escalations.loading} error={strata.error} what="Failed to load strata" cols={7}>
          {!(strata.data?.strata ?? []).length ? (
            <Empty
              title="No single-message detection results"
              body="This is a conversation run, and per-strata recall is not defined for it."
            />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Stratum</th>
                    <th className="num">Cases</th>
                    <th className="num">Scam cases</th>
                    <th className="num">Recall</th>
                    <th className="num">Pass rate</th>
                    <th className="num">False positives</th>
                    <th>Routing</th>
                  </tr>
                </thead>
                <tbody>
                  {(strata.data?.strata ?? []).map((row) => {
                    const key = String(row.strata_key ?? "");
                    const esc = escalatedKeys.get(key);
                    return (
                      <tr key={key}>
                        <td className="truncate" style={{ maxWidth: 240 }} title={key}>
                          {dash(key)}
                        </td>
                        <td className="num">{fmtInt(row.n)}</td>
                        <td className="num">{fmtInt(row.n_scam)}</td>
                        <td
                          className="num"
                          style={{
                            color:
                              row.recall === null || row.recall === undefined
                                ? "var(--text-3)"
                                : Number(row.recall) < RECALL_GATE
                                  ? "var(--critical)"
                                  : "var(--success)",
                          }}
                        >
                          {fmtPct(row.recall)}
                        </td>
                        <td className="num" style={{ color: rateColor(row.pass_rate, num(row.n)) }}>
                          {fmtPct(row.pass_rate)}
                        </td>
                        <td className="num">{fmtInt(row.false_positives)}</td>
                        <td>
                          <div className="row">
                            {esc?.escalated ? (
                              <Badge tone="critical" title={esc.reason}>
                                escalated
                              </Badge>
                            ) : (
                              <span className="muted">normal</span>
                            )}
                            {canWrite && key && (
                              <button
                                className="btn sm"
                                onClick={() =>
                                  setPending({
                                    key,
                                    escalate: !esc?.escalated,
                                    rate: row.pass_rate === null || row.pass_rate === undefined ? 0 : 1 - Number(row.pass_rate),
                                    n: num(row.n),
                                  })
                                }
                              >
                                {esc?.escalated ? "Clear" : "Escalate"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Loadable>
        <ErrorBanner error={escalations.error} what="Failed to load routing escalations" />
      </Card>

      <Card title="Language split">
        {!(detail?.by_language ?? []).length ? (
          <Empty title="No language rows" body="This run recorded no turns." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Language</th>
                  <th className="num">Turns</th>
                  <th className="num">Pass</th>
                  <th className="num">Served-ok</th>
                  <th className="num">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {(detail?.by_language ?? []).map((row) => (
                  <tr key={row.language || "(blank)"}>
                    <td>{dash(row.language)}</td>
                    <td className="num">{fmtInt(row.n)}</td>
                    <td className="num" style={{ color: rateColor(row.pass_rate, num(row.n)) }}>
                      {fmtPct(row.pass_rate)} <span className="muted">({fmtInt(row.passed)})</span>
                    </td>
                    <td className="num">{fmtInt(row.served_ok)}</td>
                    <td className="num">{fmtMs(row.avg_latency_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Confirm
        open={pending !== null}
        onClose={() => {
          setPending(null);
          setReason("");
        }}
        onConfirm={applyEscalation}
        danger={Boolean(pending?.escalate)}
        confirmLabel={pending?.escalate ? "Escalate stratum" : "Clear escalation"}
        title={pending?.escalate ? `Escalate ${pending.key}?` : `Clear escalation on ${pending?.key ?? ""}?`}
        body={
          <>
            <p>
              This changes how the router treats live traffic in this stratum. The store is append-only, so clearing
              writes a new row rather than deleting the old one.
            </p>
            <Field label="Reason" hint="Recorded beside the escalation.">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="why this stratum" />
            </Field>
          </>
        }
      />
    </div>
  );
}
